import { AxiosInstance } from 'axios';
import {
  IPatientDetails,
  Group,
} from '@zero-dash/types';
import { PseudoStatus } from '@/types/TaskDashboard/TaskDashboard.types';
import { IClinicalDashboardPatient, IClinicalDashboardSample } from '../../../../../types/Dashboard.types';
import { ClinicalStatus } from '../../../../../types/MTB/ClinicalStatus.types';
import {
  IClinicalVersionRaw, UpdateClinicalVersionBody,
} from '../../../../../types/MTB/MTB.types';
import {
  IAssignReviewerBody,
  IClinicalSample,
} from '../../../../../types/Samples/Sample.types';
import { IClinicalDashboardSearchOptions } from '../../../../../types/Search.types';

export interface IClinicalDashboardFilters {
  search?: string[];
  analysisSetIds?: string[];
  expedited?: boolean;
  gender?: string[];
  vitalStatus?: string[];
  ageRange?: number[];
  eventType?: string[];
  startMtb?: string;
  endMtb?: string;
  startEnrolment?: string;
  endEnrolment?: string;
  zero2FinalDiagnosis?: string[];
  sortColumns?: string[];
  sortDirections?: string[];
  clinicalStatus?: ClinicalStatus[];
  pseudoStatuses?: PseudoStatus[];
  assignees?: string[];
}

export interface IClinicalClient {
  getRecords(
    filters?: IClinicalDashboardFilters,
    page?: number,
    limit?: number
  ): Promise<IClinicalDashboardSample[]>;
  getRecordsCount(filters?: IClinicalDashboardFilters): Promise<number>;
  getPatientDetails(patientId: string): Promise<IPatientDetails>;
  getActiveSample(sampleId: string): Promise<IClinicalSample>;
  getClinicalVersionById(versionId: string): Promise<IClinicalVersionRaw>;
  getLatestClinicalVersion(sampleId: string): Promise<IClinicalVersionRaw>;
  updateClinicalVersionData(
    clinicalVersionId: string,
    data: UpdateClinicalVersionBody,
  ): Promise<number>;
  addReviewer(versionId: string, body: IAssignReviewerBody): Promise<void>;
  removeReviewer(versionId: string, group: Group): Promise<void>;
  updateReviewStatus(
    versionId: string,
    group: Group,
    status: string
  ): Promise<void>;
  mapFilters(
    dbFilters: IClinicalDashboardSearchOptions
  ): IClinicalDashboardFilters;
  getDashboardPatients(
    filters?: IClinicalDashboardFilters,
    page?: number,
    limit?: number,
  ): Promise<IClinicalDashboardPatient[]>;
  getDashboardPatientsCount(filters?: IClinicalDashboardFilters): Promise<number>;
}

interface IClinicalDashboardPatientResponse {
  patientId: string;
  vitalStatus: string;
  gender: string;
  publicSubjectId: string;
  cases: IClinicalDashboardSample[];
}

/** Factory for the clinical MTB SDK client bound to an Axios instance. */
export const createClinicalClient = (instance: AxiosInstance): IClinicalClient => {
  const getRecords = async (
    filters?: IClinicalDashboardFilters,
    page = 1,
    limit = 100,
  ): Promise<IClinicalDashboardSample[]> => {
    const resp = await instance.post<IClinicalDashboardSample[]>(
      '/clinical/sample',
      { ...filters, page, limit },
    );
    return resp.data;
  };

  const getRecordsCount = async (
    filters?: IClinicalDashboardFilters,
  ): Promise<number> => {
    const resp = await instance.post<number>('/clinical/sample/count', filters);
    return resp.data;
  };

  const getPatientDetails = async (
    patientId: string,
  ): Promise<IPatientDetails> => {
    const resp = await instance.get<IPatientDetails>(
      `/clinical/patient/${patientId}`,
    );
    return resp.data;
  };

  const getActiveSample = async (sampleId: string): Promise<IClinicalSample> => {
    const resp = await instance.get<IClinicalSample>(
      `/clinical/sample/${sampleId}`,
    );
    return resp.data;
  };

  const getClinicalVersionById = async (
    versionId: string,
  ): Promise<IClinicalVersionRaw> => {
    const resp = await instance.get<IClinicalVersionRaw>(
      `/clinical/sample/version/${versionId}`,
    );
    return resp.data;
  };

  const getLatestClinicalVersion = async (
    sampleId: string,
  ): Promise<IClinicalVersionRaw> => {
    const resp = await instance.get<IClinicalVersionRaw>(
      `/clinical/sample/${sampleId}/version/latest`,
    );
    return resp.data;
  };

  const updateClinicalVersionData = async (
    clinicalVersionId: string,
    data: UpdateClinicalVersionBody,
  ): Promise<number> => {
    const resp = await instance.put(`/clinical/sample/${clinicalVersionId}`, data);

    return resp.data;
  };

  const addReviewer = async (
    clinicalVersionId: string,
    body: IAssignReviewerBody,
  ): Promise<void> => {
    await instance.post(`/clinical/sample/${clinicalVersionId}/reviewer`, body);
  };

  const removeReviewer = async (
    clinicalVersionId: string,
    group: Group,
  ): Promise<void> => {
    await instance.delete(`/clinical/sample/${clinicalVersionId}/${group}`);
  };

  const updateReviewStatus = async (
    clinicalVersionId: string,
    group: Group,
    status: string,
  ): Promise<void> => {
    await instance.put(`/clinical/sample/${clinicalVersionId}/review-status`, {
      status,
      group,
    });
  };

  const mapFilters = (
    dbFilters: IClinicalDashboardSearchOptions,
  ): IClinicalDashboardFilters => {
    const {
      searchId,
      expedited,
      gender,
      vitalStatus,
      ageRange,
      eventType,
      zero2FinalDiagnosis,
      sortColumns,
      sortDirections,
      clinicalStatus,
      assignees,
    } = dbFilters;

    const filters: IClinicalDashboardFilters = {};

    if (searchId && searchId.length > 0) filters.search = searchId;

    if (expedited) filters.expedited = expedited;

    if (gender && gender.length > 0) filters.gender = gender;

    if (vitalStatus && vitalStatus.length > 0) filters.vitalStatus = vitalStatus;

    if (ageRange) {
      filters.ageRange = ageRange;
    }

    if (eventType && eventType.length > 0) {
      filters.eventType = eventType;
    }

    if (dbFilters.startMtb) {
      filters.startMtb = dbFilters.startMtb;
    }

    if (dbFilters.endMtb) {
      filters.endMtb = dbFilters.endMtb;
    }

    if (dbFilters.startEnrolment) {
      filters.startEnrolment = dbFilters.startEnrolment;
    }

    if (dbFilters.endEnrolment) {
      filters.endEnrolment = dbFilters.endEnrolment;
    }

    if (zero2FinalDiagnosis && zero2FinalDiagnosis.length > 0) {
      filters.zero2FinalDiagnosis = zero2FinalDiagnosis;
    }

    if (clinicalStatus && clinicalStatus.length > 0) {
      filters.clinicalStatus = clinicalStatus;
    }

    if (assignees && assignees.length > 0) {
      filters.assignees = assignees.map((c) => c.split('::')[1]);
    }

    if (sortColumns && sortColumns.length > 0) {
      filters.sortColumns = sortColumns;
      filters.sortDirections = sortDirections;
    }

    return filters;
  };

  const getDashboardPatients = async (
    filters?: IClinicalDashboardFilters,
    page = 1,
    limit = 20,
  ): Promise<IClinicalDashboardPatient[]> => {
    const resp = await instance.post<IClinicalDashboardPatientResponse[]>(
      '/clinical/sample/dashboard-patients',
      { ...filters, page, limit },
    );
    return resp.data.map((patient) => ({
      patientId: patient.patientId,
      vitalStatus: patient.vitalStatus,
      gender: patient.gender,
      publicSubjectId: patient.publicSubjectId,
      samples: patient.cases,
    }));
  };

  const getDashboardPatientsCount = async (
    filters?: IClinicalDashboardFilters,
  ): Promise<number> => {
    const resp = await instance.post<number>('/clinical/sample/dashboard-patients/count', filters);
    return resp.data;
  };

  return {
    getRecords,
    getRecordsCount,
    getPatientDetails,
    getActiveSample,
    getClinicalVersionById,
    getLatestClinicalVersion,
    updateClinicalVersionData,
    addReviewer,
    removeReviewer,
    updateReviewStatus,
    mapFilters,
    getDashboardPatients,
    getDashboardPatientsCount,
  };
};
